import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from 'react-query';
import { Spin, Table } from 'antd';
import { useAuth } from '@unbooking/ui-modules/lib/auth';
import { Download } from '@assets/svg/icons';
import { IDrivesTable } from '@common/interfaces/LogbookAPI';
import { useLogbookContext, useRepository, useFilterContext } from '@context';
import { useFacility } from '@common/hooks';
import { ReactComponent as ChevronDown } from '@assets/svg/icons/logbook/ChevronDown.svg';
import * as Formatters from '@common/utils/format';
import { DriveTabs } from '@common/constants/logbook';
import { AdditionalFilters, SortingDirections } from '@common/interfaces/LogbookRepository';
import { useDateFormat, useToggleBoolean, useHBHLocation } from '@common/hooks/logbook';
import { Tabs, Pagination, DatePicker, IconButton } from './components';
import { PerPageType } from './components/Pagination';
import FiltersBlock from './components/FiltersBlock';
import { tableColumns } from './TableColumns';
import Serializer from './serializer';
import './styles.scss';

export const LogbookPage: React.FC = () => {
  const [data, setData] = useState<IDrivesTable[]>([]);
  const [isFilterOpened, toggleFilters] = useToggleBoolean();

  const { facility, agenciesOptions } = useFacility();
  const { logbookRepository } = useRepository();
  const { setTabNumbers, selectedTab, datePeriod, tableState, tableDispatch, setIsCarpooled } =
    useLogbookContext();
  const { filtersState, selectFiltersForApi } = useFilterContext();

  const { Logo } = useHBHLocation();

  const isMinScreen = useMediaQuery({ maxDeviceWidth: 1366 });
  const { isAuthenticated: authState } = useAuth();

  const isFacilityMultiAgency = agenciesOptions.length > 1;

  const { data: latestData, isLoading } = useQuery(
    [
      'drives',
      tableState.selectedPage,
      tableState.perPage,
      selectedTab,
      tableState.order,
      datePeriod.first.toISOString(),
      datePeriod.last.toISOString(),
      ...filtersState.filters.map((i) => i.selected),
    ],
    () =>
      logbookRepository.getAllDrives(
        facility.facilityId,
        isFacilityMultiAgency,
        tableState.selectedPage,
        tableState.perPage,
        { driveTime: tableState.order },
        {
          to_do_drives: selectedTab === DriveTabs.toDo,
          from_date: Formatters.dateFormatForAPI(datePeriod.first),
          to_date: Formatters.dateFormatForAPI(datePeriod.last),
          additionalFilter: (selectFiltersForApi as () => AdditionalFilters)(),
        },
      ),
    {
      onError: () => {
        setData([]);
        setTabNumbers([0, 0]);
      },
      enabled: authState && Boolean(agenciesOptions.length),
      refetchOnWindowFocus: false,
    },
  );

  const { refetch, isLoading: isFileLoading } = useQuery(
    [
      'drivesCsv',
      selectedTab,
      tableState.order,
      datePeriod.first.toISOString(),
      datePeriod.last.toISOString(),
      filtersState.filters.length,
    ],
    () =>
      logbookRepository.getDrivesCsv(
        facility.facilityId,
        isFacilityMultiAgency,
        `${facility.agency}|${facility.country}|${facility.city}`,
        { driveTime: tableState.order },
        {
          to_do_drives: selectedTab === DriveTabs.toDo,
          from_date: Formatters.dateFormatForAPI(datePeriod.first),
          to_date: Formatters.dateFormatForAPI(datePeriod.last),
          additionalFilter: (selectFiltersForApi as () => AdditionalFilters)(),
        },
      ),
    {
      enabled: false,
      refetchInterval: false,
    },
  );

  const handleExportCsv = async () => {
    await refetch();
  };

  const handlePerPageChange = (payload: PerPageType) => {
    tableDispatch({ type: 'perPage', payload });
  };

  const handlePageChange = (payload: number) => {
    tableDispatch({ type: 'selectedPage', payload });
  };

  const handleOrderChange = (payload: SortingDirections) => {
    tableDispatch({ type: 'order', payload });
  };

  useEffect(() => {
    if (latestData) {
      setData(latestData?.items?.map(Serializer.drivesToTable) || []);
      setTabNumbers([
        latestData.aggregatedInfo?.doneDrivesNumber || 0,
        latestData.aggregatedInfo?.toDoDrivesNumber || 0,
      ]);
    }
  }, [latestData]);

  const navigate = useNavigate();

  const handleClick = (data: string) => {
    const [id, isCarpooled] = data.split('|');
    setIsCarpooled(isCarpooled === 'true');
    navigate(`details/${id}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTableChange = (p: any, f: any, sorter: any) => {
    handleOrderChange(sorter.order === 'descend' ? 'desc' : 'asc');
  };

  const firstDay = useDateFormat(datePeriod.first);
  const lastDay = useDateFormat(datePeriod.last);

  return (
    <div className="drives-container">
      <DatePicker />
      <Tabs />
      <div className="actions-container">
        <div className="info-container">
          <Logo fill="#E0E8ED" width={58} height={58} />
          <div className="info-text">
            <p className="info-text-subtitle">
              {facility.country}, {facility.city}
            </p>
            <p className="info-text-title">
              {firstDay} - {lastDay}
            </p>
          </div>
        </div>
        <div className="actions-buttons">
          {!!filtersState.filters.length && (
            <IconButton onClick={toggleFilters} title="Filters" color="#0A6EB4">
              <ChevronDown width="15px" height="15px" />
            </IconButton>
          )}
          <Spin wrapperClassName="actions-export" spinning={isFileLoading}>
            <IconButton
              onClick={handleExportCsv}
              title={isFileLoading ? 'Loading xls...' : 'Export View'}
              color="#30AC3E"
            >
              <Download width="15px" height="15px" />
            </IconButton>
          </Spin>
        </div>
      </div>
      {!!filtersState.filters.length && isFilterOpened && <FiltersBlock />}
      <div style={{ marginTop: 32, marginBottom: 16 }}>
        <Spin spinning={isLoading}>
          <Table
            scroll={{ x: 1350 }}
            columns={tableColumns(selectedTab === DriveTabs.toDo, isMinScreen)}
            dataSource={data}
            pagination={false}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onRow={(row: any) => ({
              onClick: () => handleClick(row.id),
            })}
            onChange={handleTableChange}
          />
        </Spin>
      </div>
      <Pagination
        selectedPage={tableState.selectedPage}
        onChangePage={handlePageChange}
        total={latestData?.count}
        perPage={tableState.perPage}
        onPerPageChange={handlePerPageChange}
        isLoading={isLoading}
      />
    </div>
  );
};

export default LogbookPage;
